import type {MonthlyAverageRate} from 'seo/getHotelAverageRate'

export interface MinMax {
  min: number
  max: number
}

/**
 * Bar heights in px. The shortest bar is deliberately non-zero so the
 * cheapest month stays readable instead of collapsing to a line.
 */
export const BAR_HEIGHTS = {
  mobile: {min: 15, max: 64},
  desktop: {min: 19, max: 80}
} as const satisfies Record<string, MinMax>

export interface MonthlyRate {
  /** Calendar month, 1 (January) through 12 (December). */
  month: number
  rate: number
}

/**
 * Picks the rate to plot and drops months the endpoint has no data for, so a
 * partial response renders the months it does cover instead of nothing.
 */
export const toMonthlyRates = (
  monthlyAverage: MonthlyAverageRate[],
  showTotalPrices: boolean
): MonthlyRate[] =>
  monthlyAverage
    .filter(({month, baseRate}) => month >= 1 && month <= 12 && baseRate > 0)
    .map(({month, baseRate, taxesFees}) => ({
      month,
      rate: showTotalPrices ? baseRate + taxesFees : baseRate
    }))
    .sort((a, b) => a.month - b.month)

/**
 * Scales bars between the cheapest and most expensive month rather than from
 * zero. Yearly averages sit in a narrow band, and a zero baseline flattens the
 * seasonality the chart exists to show.
 */
export const getBarHeight = (
  rate: number,
  rates: MinMax,
  heights: MinMax
): number => {
  if (rates.max <= rates.min) return heights.max

  const ratio = (rate - rates.min) / (rates.max - rates.min)
  return heights.min + ratio * (heights.max - heights.min)
}

/**
 *  A `Date` positioned on the given month, for `Intl` month name formatting.
 *  Use hardcoded year as we're only taking the month into account.
 *  Any non-leap year works, but 2021 is used here to avoid Feb 29.
 */
export const getMonthDate = (month: number): Date =>
  new Date(2021, month - 1, 1)

export interface MonthlyRateExtremes {
  min: number
  max: number
  cheapestMonth: number
  mostExpensiveMonth: number
}

/**
 * Keeps the first month holding each extreme, so a year with repeated highs or
 * lows still highlights exactly one bar and matches the single-month legend.
 * Callers must pass a non-empty list.
 */
export const findExtremes = ([
  first,
  ...rest
]: MonthlyRate[]): MonthlyRateExtremes =>
  rest.reduce<MonthlyRateExtremes>(
    (extremes, {month, rate}) => ({
      min: Math.min(extremes.min, rate),
      max: Math.max(extremes.max, rate),
      cheapestMonth: rate < extremes.min ? month : extremes.cheapestMonth,
      mostExpensiveMonth:
        rate > extremes.max ? month : extremes.mostExpensiveMonth
    }),
    {
      min: first.rate,
      max: first.rate,
      cheapestMonth: first.month,
      mostExpensiveMonth: first.month
    }
  )

/**
 * Column closest to `clientX`, so a finger between two bars still picks one.
 * Compares centres instead of hit-testing, which keeps it right in RTL where
 * the DOM order and the on-screen order disagree.
 */
export const findNearestColumn = (centers: number[], clientX: number): number =>
  centers.reduce(
    (nearest, center, index) =>
      Math.abs(center - clientX) < Math.abs(centers[nearest] - clientX)
        ? index
        : nearest,
    0
  )
